import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';
import { downscaleImage } from './image-preprocessing';
import { randomUuid } from './random-uuid';
import { apiUrl, authHeaders } from './shell';
import { enqueueComposerUpload } from './business-file-attachments';

export type StoredImage = {
  id: string;
  name: string;
  mimeType: string;
  width: number;
  height: number;
};
type ImageReceipt = {
  threadId: string;
  scope: string;
  id: string;
  normalized: Promise<File>;
  upload?: Promise<StoredImage>;
  image?: StoredImage;
};
const imageReceipts = new WeakMap<File, ImageReceipt>();
const errorMessage = async (response: Response) => {
  const body = await response.json().catch(() => null);
  return typeof body?.message === 'string'
    ? body.message
    : 'The image could not be uploaded. Remove it or choose another image.';
};
export async function uploadChatImage(
  threadId: string,
  id: string,
  file: File,
): Promise<StoredImage> {
  const created = await fetch(apiUrl('/api/v1/threads'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: threadId }),
  });
  if (!created.ok) throw new Error(await errorMessage(created));
  const response = await fetch(
    apiUrl(`/api/v1/threads/${threadId}/attachments/${id}?name=${encodeURIComponent(file.name)}`),
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { ...authHeaders(), 'Content-Type': file.type },
      body: file,
    },
  );
  if (!response.ok) throw new Error(await errorMessage(response));
  return (await response.json()).attachment as StoredImage;
}

/** One actual upload per selected File; send and edit keep its private reference.
 * Removal never deletes original bytes retained by an older message branch. */
export class StoredChatImageAdapter implements AttachmentAdapter {
  accept = 'image/*';
  constructor(
    private readonly options: {
      threadId: string;
      isCurrentOwner: () => boolean;
      scope: () => string;
      upload?: typeof uploadChatImage;
    },
  ) {}
  private current() {
    if (!this.options.isCurrentOwner())
      throw new Error('Your workspace changed. Choose the image again.');
  }
  async *add({ file }: { file: File }): AsyncGenerator<PendingAttachment, void> {
    this.current();
    const scope = this.options.scope();
    if (!scope) throw new Error('Sign in to attach an image.');
    if (file.size > 16 * 1024 * 1024)
      throw new Error('Choose an image under 16 MiB, or remove it to continue.');
    const receipt: ImageReceipt = imageReceipts.get(file) ?? {
      threadId: this.options.threadId,
      scope,
      id: randomUuid(),
      normalized: downscaleImage(file),
    };
    if (receipt.threadId !== this.options.threadId || receipt.scope !== scope)
      throw new Error('Your workspace changed. Choose the image again.');
    imageReceipts.set(file, receipt);
    const normalized = await receipt.normalized;
    imageReceipts.set(normalized, receipt);
    this.current();
    const id = receipt.id;
    const pending: PendingAttachment = {
      id,
      name: file.name,
      type: 'image',
      contentType: normalized.type,
      file: normalized,
      status: { type: 'running', reason: 'uploading', progress: 0 },
    };
    yield pending;
    // Keep the actual selected original in storage. The preview is derived;
    // the server independently creates and checks the model rendition.
    const saved = await (receipt.upload ??= enqueueComposerUpload(async () => {
      this.current();
      if (scope !== this.options.scope())
        throw new Error('Your workspace changed. Choose the image again.');
      return (this.options.upload ?? uploadChatImage)(this.options.threadId, id, file);
    }));
    this.current();
    if (scope !== this.options.scope())
      throw new Error('Your workspace changed. Choose the image again.');
    receipt.image = saved;
    yield { ...pending, status: { type: 'requires-action', reason: 'composer-send' } };
  }
  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    this.current();
    const receipt = imageReceipts.get(attachment.file);
    if (
      !receipt ||
      !receipt.image ||
      receipt.scope !== this.options.scope() ||
      receipt.threadId !== this.options.threadId
    )
      throw new Error('The image is unavailable. Remove it or choose it again.');
    const saved = receipt.image;
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [
        {
          type: 'file',
          mimeType: saved.mimeType,
          filename: saved.name,
          data: `/api/v1/threads/${encodeURIComponent(this.options.threadId)}/attachments/${encodeURIComponent(saved.id)}/content`,
        },
      ],
    };
  }
  async remove() {}
}
